import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ROUTES } from 'config/routes'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import type { Booking } from 'repositories/BerthApi/types/booking'

import { Snackbar } from 'components/Snackbar'
import { useSnackbar } from 'components/Snackbar/useSnackbar'
import { useBerthManagements } from 'containers/SpBerthManagement/hooks/useBerthManagements'
import { useTechTouchSync } from 'hooks/useTechTouch'
import loginStorage from 'utils/loginStorage'
import { logout } from 'utils/logout'
import { setPostLoginRedirect } from 'utils/postLoginRedirect'

import { AppHeader } from './components/AppHeader'
import { BerthSelector } from './components/BerthSelector'
import { CardDetailModal } from './components/CardDetailModal'
import { DateNavigator } from './components/DateNavigator'
import { EditBookingModal } from './components/EditBookingModal'
import { Timeline } from './components/Timeline'
import { UnassignedArea } from './components/UnassignedArea'
import { UnassignedDetailModal } from './components/UnassignedDetailModal'
import { ZoomControl } from './components/ZoomControl'
import { useBerthGroups } from './hooks/useBerthGroups'
import { useBookingMasterData } from './hooks/useBookingMasterData'
import { useListBerthsByTenant } from './hooks/useListBerthsByTenant'
import { useUnassignedBookings } from './hooks/useUnassignedBookings'
import { useTenantSetting } from './hooks/useTenantSetting'
import { useTimelineScale } from './hooks/useTimelineScale'
import styles from './index.module.css'
import { getBookingKey, toCard } from './utils/berthManagementHelpers'

export const SpBerthManagement: React.FC = () => {
  const { pathname, search } = useLocation()
  const history = useHistory()
  const params = new URLSearchParams(search)
  const isAuthenticated = !!loginStorage.getAuthData()

  // 未ログインでアクセスした場合、ログイン後に元のスマバURLへ戻れるよう戻り先を保存する
  useEffect(() => {
    if (!isAuthenticated) {
      setPostLoginRedirect(`${pathname}${search}`)
    }
  }, [isAuthenticated, pathname, search])

  const tenantId = isAuthenticated ? params.get('tenant_id') : null
  const warehouseId = isAuthenticated ? params.get('warehouse_id') : null
  const userId = isAuthenticated ? localStorage.getItem('userId') : null
  const berthId = params.get('berth_id')
  const date = params.get('date') || moment().format('YYYY-MM-DD')

  useTechTouchSync(undefined, warehouseId, tenantId, undefined, userId)

  const {
    canSpBerthManagement,
    canUnmannedCheckin,
    isLoading: tenantLoading,
    error: tenantError,
  } = useTenantSetting(tenantId)
  const {
    berths,
    isLoading: berthsLoading,
    error: berthsError,
  } = useListBerthsByTenant(tenantId, warehouseId)
  const { berthGroups, error: berthGroupsError } = useBerthGroups(tenantId)
  const { masterData, error: masterDataError } = useBookingMasterData(tenantId)
  const {
    assignedCards,
    bookingsMap,
    error: berthManagementsError,
  } = useBerthManagements(tenantId, warehouseId, berthId, date, masterData)

  const {
    unassignedBookings,
    isLoading: unassignedLoading,
    error: unassignedError,
  } = useUnassignedBookings(tenantId, warehouseId, date)

  // 未割り当て予約を BerthCard[] に変換
  const unassignedCards = React.useMemo(
    () => unassignedBookings.map((b) => toCard(b, masterData)),
    [unassignedBookings, masterData]
  )
  const unassignedBookingsMap = React.useMemo(() => {
    const map: Record<string, Booking> = {}
    for (const b of unassignedBookings) {
      map[getBookingKey(b)] = b
    }
    return map
  }, [unassignedBookings])

  // バース予約一覧モーダル用
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const { snackbar, notify } = useSnackbar()
  const handleCardTap = (cardId: string) => setSelectedCardId(cardId)
  const handleCloseModal = () => setSelectedCardId(null)

  // 未割り当て予約一覧モーダル用
  const [selectedUnassignedCardId, setSelectedUnassignedCardId] = useState<string | null>(null)
  const handleUnassignedCardTap = (cardId: string) => setSelectedUnassignedCardId(cardId)
  const handleCloseUnassignedModal = () => setSelectedUnassignedCardId(null)

  // 予約編集モーダル用
  const [editingTarget, setEditingTarget] = useState<{
    bookingId: string
    isUnassigned: boolean
  } | null>(null)
  const handleEditAssigned = (bookingKey: string) => {
    const b = bookingsMap[bookingKey]
    if (!b?.bookingId) return
    setEditingTarget({ bookingId: b.bookingId, isUnassigned: false })
  }
  const handleEditUnassigned = (bookingKey: string) => {
    const b = unassignedBookingsMap[bookingKey]
    if (!b?.bookingId) return
    setEditingTarget({ bookingId: b.bookingId, isUnassigned: true })
  }
  const handleCloseEditModal = () => setEditingTarget(null)

  // タイムラインの拡大・縮小
  const timelineScrollRef = useRef<HTMLDivElement>(null)
  const { pxPerMinute, canIncrement, canDecrement, increment, decrement, scrollTopBeforeScaleRef } =
    useTimelineScale(timelineScrollRef)

  useEffect(() => {
    if (
      tenantError ||
      berthsError ||
      berthGroupsError ||
      masterDataError ||
      berthManagementsError ||
      unassignedError
    ) {
      logout()
    }
  }, [
    tenantError,
    berthsError,
    berthGroupsError,
    masterDataError,
    berthManagementsError,
    unassignedError,
  ])

  // berth_id がクエリにない場合は、最初のバースを選択状態にする
  useEffect(() => {
    if (!berthId && berths.length > 0) {
      const newParams = new URLSearchParams(search)
      newParams.set('berth_id', berths[0].id?.toString() || '')
      history.replace({ search: newParams.toString() })
    }
  }, [berths, berthId, search, history])

  if (!isAuthenticated) {
    return <Redirect to={ROUTES.login} />
  }

  if (!tenantLoading && !canSpBerthManagement) {
    return <Redirect to={ROUTES.notFound} />
  }

  const handleBerthChange = (newBerthId: string) => {
    const newParams = new URLSearchParams(search)
    newParams.set('berth_id', newBerthId)
    history.push({ search: newParams.toString() })
  }

  const handleDateChange = (newDate: string) => {
    const newParams = new URLSearchParams(search)
    newParams.set('date', newDate)
    history.push({ search: newParams.toString() })
  }

  const selectedBerth = berths.find((b) => b.id === berthId)
  const isBerthNotFound = berthId && !berthsLoading && berths.length > 0 && !selectedBerth

  return (
    <div className={styles.pageWrapper}>
      <AppHeader />
      <UnassignedArea
        cards={unassignedCards}
        isLoading={unassignedLoading}
        onCardTap={handleUnassignedCardTap}
      />
      <BerthSelector
        berths={berths}
        berthGroups={berthGroups}
        selectedBerthId={berthId}
        onChange={handleBerthChange}
        isBerthNotFound={!!isBerthNotFound}
      />
      <div className={styles.headerWrapper}>
        <div className={styles.dateNavigatorWrapper}>
          <DateNavigator date={date} onChange={handleDateChange} />
        </div>
        <ZoomControl
          canIncrement={canIncrement}
          canDecrement={canDecrement}
          onIncrement={increment}
          onDecrement={decrement}
        />
      </div>
      <Timeline
        cards={assignedCards}
        date={date}
        onCardTap={handleCardTap}
        pxPerMinute={pxPerMinute}
        scrollRef={timelineScrollRef}
        scrollTopBeforeScaleRef={scrollTopBeforeScaleRef}
      />
      <CardDetailModal
        berthName={selectedBerth?.name || ''}
        date={date}
        cards={assignedCards}
        bookingsMap={bookingsMap}
        masterData={masterData}
        tenantId={tenantId}
        selectedCardId={selectedCardId}
        onClose={handleCloseModal}
        onNotify={notify}
        onEditClick={handleEditAssigned}
      />
      <UnassignedDetailModal
        berthId={berthId || ''}
        berths={berths}
        date={date}
        cards={unassignedCards}
        bookingsMap={unassignedBookingsMap}
        masterData={masterData}
        tenantId={tenantId}
        selectedCardId={selectedUnassignedCardId}
        canUnmannedCheckin={canUnmannedCheckin ?? false}
        onClose={handleCloseUnassignedModal}
        onNotify={notify}
        onEditClick={handleEditUnassigned}
      />
      <EditBookingModal
        bookingId={editingTarget?.bookingId ?? null}
        berths={berths}
        tenantId={tenantId}
        warehouseId={warehouseId}
        isUnassigned={editingTarget?.isUnassigned ?? false}
        onClose={handleCloseEditModal}
        onNotify={notify}
      />
      <Snackbar {...snackbar} />
    </div>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
})

// NOTE: 現状SpBerthManagementでしかReact Queryを利用しておらず影響範囲を最小限にするため、ここでQueryClientProviderでラップしている
export const SpBerthManagementPage: React.FC = (props) => (
  <QueryClientProvider client={queryClient}>
    <SpBerthManagement {...props} />
  </QueryClientProvider>
)
